import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DeleteDialog, Modal } from "components";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import { useValidateIsNotEmptyArray } from "formvalidation/hooks";
import ProductEdit from "Products/ProductEdit";
import ProductsTable from "Products/ProductsTable";
import React, { useState } from "react";

interface Props {
  productsByParentQueryKey: any[]
  documentType: server.dto.DocumentTypes
  documentId: number
  productsPanel: server.dto.ProductsPanel
  validatorKey?: string

}

export const useProductsByParentQuery = (documentType: server.dto.DocumentTypes, documentId: number, queryKey: any[], initialData: server.dto.ProductsByParent) => {
  return useQuery({
    queryFn: async () => window.CoreApi.Product.ListByParent({ mainArea: documentType, parentId: documentId }).then(r => r.data.data),
    queryKey: queryKey,
    initialData: initialData,
    keepPreviousData: true,
  });
}

const ProductsPanel = (props: Props) => {
  const [editingProduct, setEditingProduct] = useState<server.dto.Product>(undefined);
  const [deletingProduct, setDeletingProduct] = useState<server.dto.Product>(undefined);

  const productsByParentQuery = useProductsByParentQuery(props.documentType, props.documentId, props.productsByParentQueryKey, props.productsPanel.data);

  const queryClient = useQueryClient();
  const reorderProductsMutation = useMutation({
    mutationFn: async (productsByParent: server.dto.ProductsByParent) => {
      return window.CoreApi.Product.StoreSorted({
        mainArea: props.documentType,
        parentId: props.documentId,
        productIds: productsByParent.collection.map(p => p.productId)
      });
    },
    mutationKey: ["reorderProducts", props.documentType, props.documentId],
    onMutate: async (newProductsByParent: server.dto.ProductsByParent) => {
      await queryClient.cancelQueries(props.productsByParentQueryKey);
      const previousData = queryClient.getQueryData(props.productsByParentQueryKey);
      queryClient.setQueryData(props.productsByParentQueryKey, newProductsByParent);
      return { previousData };
    },
    onError: (_, __, context) => {
      queryClient.setQueryData(props.productsByParentQueryKey, context.previousData);
    },
    onSettled: () => {
      queryClient.invalidateQueries(props.productsByParentQueryKey);
    }
  });

  const onProductDeleteClicked = (product: server.dto.Product) => {
    setDeletingProduct(product);
  };

  const onProductDeleted = async () => {
    const res = await window.CoreApi.Product.Delete({ productId: deletingProduct.productId });
    if (res.data.hasErrors) {
      window.displayGenericModal(RESX.ExceptionMessage.ThereIsAProblem, RESX.ExceptionMessage.ThereWasAnError, "tiny", "red");
      return;
    }

    productsByParentQuery.refetch();
    setDeletingProduct(undefined);
  };

  const productsValidation = useValidateIsNotEmptyArray({
    key: props.validatorKey,
    title: RESX.Product.Products,
    value: productsByParentQuery.data.collection
  })

  return <SectionPanel.Panel
    panelRef={productsValidation.ref}
    header={<>
      <PanelSegment.Title title={RESX.Product.Products} />
      <PanelSegment.Button onClick={() => { setEditingProduct(null); }} label={RESX.GeneralLabels.resxNew} type="secondary" />
    </>}
  >
    {productsByParentQuery.data.collection.length > 0 && <ProductsTable
      onEditClicked={setEditingProduct}
      onDeleteClicked={onProductDeleteClicked}
      productsByParent={productsByParentQuery.data}
      productsPanelConfig={props.productsPanel.config}
      onProductsOrderChanged={reorderProductsMutation.mutate}
    />}
    {productsByParentQuery.data.collection.length === 0 && <NoDataAvailableDiv error={productsValidation.error.hasError} />}

    <DeleteDialog
      isOpen={deletingProduct != null}
      message={RESX.GeneralWarnings.AreYouSure}
      handleClose={() => { setDeletingProduct(null); }}
      onDelete={onProductDeleted} />

    <Modal
      isOpen={editingProduct !== undefined}
      title={editingProduct == null ? RESX.Product.NewProduct : RESX.Product.EditProduct}
      width="huge"
      height={780}
      handleClose={() => { setEditingProduct(undefined); }}
    >
      <ProductEdit
        initialProduct={editingProduct}
        productsConfig={props.productsPanel.config}
        productsByParent={productsByParentQuery.data}
        onProductSaved={() => {
          productsByParentQuery.refetch();
          setEditingProduct(undefined);
        }}
        mainArea={props.documentType}
      />
    </Modal>
  </SectionPanel.Panel>
}

export default ProductsPanel;